import React from 'react';
import type { FormikConfig } from 'formik';
import type { Participation, ParticipationType, ReasonEnd, ReasonStart } from 'app/types';
import type { UseQueryRefetch } from 'app/types/useQuery.types';
import { useEmployeeCreateParticipationMutation, useEmployeeUpdateParticipationMutation, useSnakeBar } from 'app/hooks';
import { useProcessCustomBackendError } from 'app/hooks/useProcessCustomBackendError';
import type { ParticipationFormValues } from '../../../EmployeeIncomeParticipations.types';

type UseParticipationFormSubmitProps = {
  isEdit: boolean;
  connectionId: string | undefined;
  employeeId: string | undefined;
  participation: Participation | null;
  refetchParticipations: UseQueryRefetch;
  closeDialog: () => void;
};

export const useParticipationFormSubmit = ({
  isEdit,
  connectionId,
  employeeId,
  participation,
  refetchParticipations,
  closeDialog,
}: UseParticipationFormSubmitProps) => {
  const { showSuccessSnakeBar } = useSnakeBar();
  const processError = useProcessCustomBackendError();

  const { mutate: createParticipation } = useEmployeeCreateParticipationMutation();
  const { mutate: updateParticipation } = useEmployeeUpdateParticipationMutation();

  const formikOnSubmit = React.useCallback<FormikConfig<ParticipationFormValues>['onSubmit']>(
    (values, { setSubmitting, resetForm }) => {
      if (!connectionId || !employeeId) {
        setSubmitting(false);
        return;
      }

      const onSuccess = (method: string) => () => {
        setSubmitting(false);
        resetForm();
        closeDialog();
        showSuccessSnakeBar({ method });
        refetchParticipations();
      };

      const onError = (method: string) => (error: any) => {
        setSubmitting(false);
        processError(error, method);
      };

      if (isEdit) {
        const method = 'updateParticipation';
        updateParticipation(
          {
            connectionId,
            employeeId,
            participationId: participation!.mongoId,
            participation: {
              endDate: values.endDate ?? null,
              reasonEnd: (values.reasonEnd as ReasonEnd) || null,
            },
          },
          {
            onSuccess: onSuccess(method),
            onError: onError(method),
          },
        );
      } else {
        const method = 'createParticipation';
        createParticipation(
          {
            connectionId,
            employeeId,
            participation: {
              startDate: values.startDate!,
              reasonStart: values.reasonStart as ReasonStart,
              type: values.type as ParticipationType,
            },
          },
          {
            onSuccess: onSuccess(method),
            onError: onError(method),
          },
        );
      }
    },
    [
      connectionId,
      employeeId,
      isEdit,
      participation,
      updateParticipation,
      createParticipation,
      closeDialog,
      showSuccessSnakeBar,
      refetchParticipations,
      processError,
    ],
  );

  return { formikOnSubmit };
};
